import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { STATUS_CONFIG, formatCurrency, formatDateIST, formatTimeIST } from "./pharmacyConstants";

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: String(status), color: "text-slate-500", bg: "bg-slate-50", border: "border-slate-200", dot: "bg-slate-400" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} /> {cfg.label}
    </span>
  );
}

export function PharmacyDetailedTable({ orders, page, setPage, loading }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="detailed-table-section">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-800">Detailed Bill View ({orders.total} items)</h2>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          Page {orders.page} of {orders.total_pages || 1}
          <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="detail-prev-page"><ChevronLeft className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="ghost" disabled={page >= orders.total_pages} onClick={() => setPage(p => p + 1)} data-testid="detail-next-page"><ChevronRight className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {["Date", "Order ID", "Status", "Type", "MRN", "Patient", "Doctor", "Medicine Name", "Batch", "Qty", "MRP", "Unit Price", "Tax", "Discount", "Med Total", "Order Total"].map(h => (
                <TableHead key={h} className="text-[10px] font-bold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={16} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>}
            {!loading && orders.data.length === 0 && <TableRow><TableCell colSpan={16} className="text-center py-10 text-slate-400">No detailed data found</TableCell></TableRow>}
            {!loading && orders.data.map((r, i) => {
              const prevRow = i > 0 ? orders.data[i - 1] : null;
              const isNewOrder = !prevRow || prevRow.orderId !== r.orderId;
              return (
                <TableRow key={i} className={`hover:bg-slate-50 transition-colors ${isNewOrder && i > 0 ? "border-t-2 border-slate-200" : ""}`} data-testid={`detail-row-${i}`}>
                  <TableCell className="text-xs whitespace-nowrap">
                    {isNewOrder ? (
                      <><div>{formatDateIST(r.createdAt)}</div><div className="text-[10px] text-slate-400">{formatTimeIST(r.createdAt)}</div></>
                    ) : <span className="text-slate-300">{"\u2193"}</span>}
                  </TableCell>
                  <TableCell className="text-xs font-mono font-semibold text-slate-700">{isNewOrder ? r.orderId : ""}</TableCell>
                  <TableCell>{isNewOrder ? <StatusBadge status={r.status} /> : null}</TableCell>
                  <TableCell className="text-xs">{isNewOrder ? r.patientType : ""}</TableCell>
                  <TableCell className="text-xs font-mono">{isNewOrder ? r.mrn : ""}</TableCell>
                  <TableCell className="text-xs max-w-[120px] truncate">{isNewOrder ? r.patientName : ""}</TableCell>
                  <TableCell className="text-xs max-w-[100px] truncate">{isNewOrder ? r.doctorName : ""}</TableCell>
                  <TableCell className="text-xs font-medium text-slate-800 max-w-[180px] truncate">{r.medicineName}</TableCell>
                  <TableCell className="text-xs text-slate-500 font-mono">{r.batchNumber || "\u2014"}</TableCell>
                  <TableCell className="text-xs text-center tabular-nums">{r.quantity}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{formatCurrency(r.mrp)}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{formatCurrency(r.unitPrice)}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{formatCurrency(r.medTax)}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums">{formatCurrency(r.medDiscount)}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums font-semibold">{formatCurrency(r.medTotal)}</TableCell>
                  <TableCell className="text-xs text-right tabular-nums font-semibold text-teal-700">{isNewOrder ? formatCurrency(r.grandTotal) : ""}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
